import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { baseUrl } from './useEndpoints';

export type TicketType = 'bug' | 'question' | 'feedback' | 'other';
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high';

export interface SupportTicket {
  _id: string;
  authorType: 'user' | 'director';
  authorId: string;
  authorEmail: string;
  authorName: string;
  directorId: string;
  type: TicketType;
  subject: string;
  body: string;
  status: TicketStatus;
  priority: TicketPriority;
  unreadCountUser: number;
  unreadCountStaff: number;
  lastMessageAt: string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessage {
  _id: string;
  ticketId: string;
  authorType: 'user' | 'staff';
  authorId: string;
  authorName: string;
  body: string;
  attachments: string[];
  readAt?: string;
  createdAt: string;
}

interface PaginatedTickets {
  docs: SupportTicket[];
  totalDocs: number;
  page: number;
  totalPages: number;
}

const QUERY_KEY = ['support'];

export const useSupportTickets = () =>
  useQuery({
    queryKey: [...QUERY_KEY, 'list'],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedTickets>(
        `${baseUrl}/support/tickets/me`,
      );
      return data;
    },
  });

export const useSupportTicketDetail = (ticketId: string | undefined) =>
  useQuery({
    enabled: !!ticketId,
    queryKey: [...QUERY_KEY, 'detail', ticketId],
    queryFn: async () => {
      const { data } = await axios.get<{
        ticket: SupportTicket;
        messages: SupportMessage[];
      }>(`${baseUrl}/support/tickets/${ticketId}`);
      return data;
    },
    refetchInterval: 10_000, // polling 10s solo mentre thread aperto
  });

export const useSupportUnreadCount = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: [...QUERY_KEY, 'unread'],
    queryFn: async () => {
      const { data } = await axios.get<{ count: number }>(
        `${baseUrl}/support/tickets/me/unread-count`,
      );
      return data.count;
    },
    refetchInterval: 30_000, // 30s background poll
  });

export const useCreateSupportTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      type: TicketType;
      subject: string;
      body: string;
    }) => {
      const { data } = await axios.post<SupportTicket>(
        `${baseUrl}/support/tickets`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};

export const useSendSupportMessage = (ticketId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const { data } = await axios.post<SupportMessage>(
        `${baseUrl}/support/tickets/${ticketId}/messages`,
        { body },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, 'detail', ticketId] });
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, 'list'] });
    },
  });
};
